import React from "react";
import {
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  IconButton,
  Button,
  Badge,
  Skeleton,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CircleIcon from "@mui/icons-material/Circle";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import ReceiptIcon from "@mui/icons-material/Receipt";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import moment from "moment";
import { useNotifications } from "@/hooks/useNotifications";
import { useNavigate } from "react-router-dom";

interface NotificationsPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  notifications: any[];
  loading: boolean;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
}

export const NotificationsPopover: React.FC<NotificationsPopoverProps> = ({
  anchorEl,
  onClose,
  notifications,
  loading,
  markAsRead,
  markAllAsRead,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          sx: {
            width: 360,
            maxHeight: 500,
            borderRadius: 3,
            mt: 1.5,
            boxShadow: theme.shadows[10],
            backgroundColor: "background.paper",
            backdropFilter: "blur(20px)",
            display: "flex",
            flexDirection: "column",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Typography variant="h6" fontWeight="600">
          Мэдэгдэл
        </Typography>
        {notifications.some((n: any) => n.status === "UNREAD" || !n.readAt) && (
          <Button
            size="small"
            startIcon={<CheckCircleOutlineIcon fontSize="small" />}
            onClick={markAllAsRead}
            sx={{
              textTransform: "none",
              color: theme.palette.text.secondary,
              "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
            }}
          >
            Бүгдийг уншсан
          </Button>
        )}
      </Box>
      <Divider />
      <List
        sx={{
          p: 0,
          flexGrow: 1,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
            borderRadius: 3,
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
            },
          },
        }}
      >
        {loading ? (
          <Box sx={{ p: 2 }}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ display: "flex", mb: 2, alignItems: "center" }}>
                <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="80%" />
                  <Skeleton variant="text" width="60%" />
                </Box>
              </Box>
            ))}
          </Box>
        ) : notifications.length > 0 ? (
          <>
            {notifications.map((notification: any) => {
              const isUnread = notification.status === "UNREAD" || !notification.readAt;

              const getNotificationIcon = () => {
                if (notification.icon) return <img src={notification.icon} alt="icon" style={{ width: 24, height: 24, objectFit: 'contain' }} />;
                switch (notification.type) {
                  case 'success': return <CheckCircleIcon fontSize="small" />;
                  case 'error': return <ErrorIcon fontSize="small" />;
                  case 'warning': return <WarningIcon fontSize="small" />;
                }
                switch (notification.entityType) {
                  case 'CONTRACT': return <DescriptionIcon fontSize="small" />;
                  case 'CUSTOMER': return <PersonIcon fontSize="small" />;
                  case 'INVOICE':
                  case 'PAYMENT':
                  case 'EBARIMT': return <ReceiptIcon fontSize="small" />;
                  case 'TICKET': return <ConfirmationNumberIcon fontSize="small" />;
                  default: return <NotificationsIcon fontSize="small" />;
                }
              };

              const getNotificationColor = () => {
                switch (notification.type) {
                  case 'success': return theme.palette.success.main;
                  case 'error': return theme.palette.error.main;
                  case 'warning': return theme.palette.warning.main;
                  case 'info': return theme.palette.info.main;
                  default: return theme.palette.primary.main;
                }
              };

              const notifColor = getNotificationColor();

              return (
                <ListItemButton
                  key={notification.id}
                  onClick={() => {
                    if (isUnread) markAsRead(notification.id);

                    if (notification.link) {
                      navigate(notification.link);
                    } else if (notification.entityType && notification.entityId) {
                      switch (notification.entityType) {
                        case "CONTRACT":
                          navigate(`/contracts/view/${notification.entityId}`);
                          break;
                        case "CUSTOMER":
                          navigate(`/customer/${notification.entityId}/grid`);
                          break;
                        case "INVOICE":
                        case "PAYMENT":
                        case "EBARIMT":
                          navigate(`/invoice/${notification.entityId}`);
                          break;
                        case "TICKET":
                          navigate(`/tickets`);
                          break;
                        case "PRODUCT":
                        case "PACKAGE":
                          navigate(`/products`);
                          break;
                        default:
                          break;
                      }
                    }
                    onClose();
                  }}
                  sx={{
                    p: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: isUnread
                      ? alpha(notifColor, 0.04)
                      : "transparent",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: alpha(notifColor, 0.08),
                    },
                    alignItems: "flex-start",
                  }}
                >
                  <ListItemAvatar sx={{ minWidth: 50 }}>
                    <Avatar
                      sx={{
                        backgroundColor: isUnread
                          ? notifColor
                          : theme.palette.action.disabledBackground,
                        color: isUnread ? "#fff" : theme.palette.text.secondary,
                      }}
                    >
                      {getNotificationIcon()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: isUnread ? 600 : 400,
                          color: theme.palette.text.primary,
                          mb: 0.5,
                        }}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mb: 0.5,
                          }}
                        >
                          {notification.body}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.disabled }}
                        >
                          {moment(notification.createdAt).fromNow()}
                        </Typography>
                      </Box>
                    }
                  />
                  {isUnread && (
                    <CircleIcon
                      sx={{
                        color: notifColor,
                        fontSize: 10,
                        ml: 2,
                        mt: 1,
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              px: 3,
              textAlign: "center",
            }}
          >
            <NotificationsIcon
              sx={{ fontSize: 48, color: theme.palette.action.disabled, mb: 2 }}
            />
            <Typography variant="body1" color="text.secondary" fontWeight={500}>
              Одоогоор мэдэгдэл алга байна.
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              Шинэ мэдэгдэл ирэх үед энд харагдах болно.
            </Typography>
          </Box>
        )}
      </List>
    </Popover>
  );
};
